import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {
    Button, Dropdown, Icon, Input, Loader, Message,
} from 'semantic-ui-react';

import {getAuthToken, getClientAud} from '../../selectors';
import {Donut, LineChart, StackedBars} from './metrics/charts';

const CUSTOM = 'custom';

const PERIODS = [
    {key: 'today', text: 'Heute', value: 'today'},
    {key: '7d', text: 'Letzte 7 Tage', value: '7d'},
    {key: '30d', text: 'Letzte 30 Tage', value: '30d'},
    {key: '90d', text: 'Letzte 90 Tage', value: '90d'},
    {key: CUSTOM, text: 'Custom', value: CUSTOM},
];

// Categorical chart colours, checked for colour-blind separation as a set and
// always used in this order. Text never takes these colours.
const BLUE = '#2a78d6';
const ORANGE = '#eb6834';
const GREEN = '#1baf7a';

// Icon badge tints: [background, icon colour].
const TINT = {
    blue: ['#e6f0fc', '#2a78d6'],
    coral: ['#fbe7e5', '#b8544f'],
    green: ['#e3f5ee', '#138a60'],
    orange: ['#fdebe3', '#c9521f'],
};

const REASONS_COLLAPSED = 5;

const number = (v) => (v || 0).toLocaleString('de-DE');

const euros = (cents) => ((cents || 0) / 100).toLocaleString('de-DE', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2,
});

// The date *label* in the header arrives ready-made from the server:
// formatting a Berlin timestamp here would redraw it on the reader's own clock,
// which is how German midnight once turned into "3:00 AM" for an admin in UTC+5.
//
// These two helpers only prefill the pickers, and <input type="date"> wants
// YYYY-MM-DD. They use the browser's own day, which can be one ahead of Berlin
// late at night - the server trims the range to today and the label then shows
// what was really used.
function isoDay(date) {
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function daysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return isoDay(d);
}

const Badge = ({icon, tint, round}) => (
    <div className={`kz-badge${round ? ' round' : ''}`}
        style={{background: TINT[tint][0], color: TINT[tint][1]}}>
        <Icon name={icon} fitted />
    </div>
);

const Kpi = ({label, value, caption, icon, tint, cy}) => (
    <div className="kz-card kz-kpi" data-cy={cy}>
        <div className="kz-kpi-top"><Badge icon={icon} tint={tint} /><span>{label}</span></div>
        <div className="kz-kpi-value">{value}</div>
        <div className="kz-caption">{caption}</div>
    </div>
);

const Mini = ({label, value, icon, tint, cy}) => (
    <div className="kz-card kz-mini" data-cy={cy}>
        <Badge icon={icon} tint={tint} round />
        <div><div className="kz-mini-value">{value}</div><div className="kz-mini-label">{label}</div></div>
    </div>
);

const Legend = ({items}) => (
    <div className="kz-legend">
        {items.map(([name, color]) => <span key={name}><i style={{background: color}} />{name}</span>)}
    </div>
);

const CardHead = ({title, sub, total, children}) => (
    <div className="kz-card-head">
        <div><h3>{title}</h3><div className="kz-sub">{sub}</div></div>
        {total !== undefined && <div className="kz-total"><b>{total}</b><span>im Zeitraum</span></div>}
        {children}
    </div>
);

const Section = ({children}) => <div className="kz-section"><b />{children}</div>;

const MetricsAdmin = () => {
    const authToken = useSelector(getAuthToken);
    const clientAud = useSelector(getClientAud);

    const [period, setPeriod] = useState('today');
    const [customFrom, setCustomFrom] = useState(daysAgo(6));
    const [customTo, setCustomTo] = useState(daysAgo(0));

    // What is actually being shown. Kept apart from the pickers so that typing
    // a date does not fire a request per keystroke - a custom range is only
    // fetched once "Anzeigen" is pressed.
    const [query, setQuery] = useState({period: 'today'});

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allReasons, setAllReasons] = useState(false);

    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const authHeaders = useCallback((extra = {}) => (
        authToken
            ? {Authorization: `Bearer ${authToken}`, JWT_AUD: clientAud, ...extra}
            : {...extra}
    ), [authToken, clientAud]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams(query);
            const res = await fetch(`/api/v1/metrics?${params}`, {
                headers: authHeaders(),
                credentials: 'include',
            });
            if (!res.ok) throw new Error(`Fehler beim Laden (${res.status})`);
            const json = await res.json();
            if (mounted.current) setReport(json);
        } catch (e) {
            if (mounted.current) setError(e.message);
        } finally {
            if (mounted.current) setLoading(false);
        }
    }, [authHeaders, query]);

    useEffect(() => { load(); }, [load]);

    const choosePeriod = (value) => {
        setPeriod(value);
        // The fixed ranges need nothing more from the admin, so show them at
        // once; a custom range waits for two dates and the button.
        if (value !== CUSTOM) setQuery({period: value});
    };

    // The pickers' min/max stop the calendar from offering these, but a date
    // can still be typed in, so the button checks too. ISO dates compare
    // correctly as strings. The server trims and falls back as well; this only
    // keeps the admin from asking for a range that cannot be shown.
    const today = daysAgo(0);
    const customProblem = !customFrom || !customTo ? 'Bitte beide Daten wählen.'
        : customFrom > today || customTo > today ? 'Daten in der Zukunft sind nicht möglich.'
        : customFrom > customTo ? 'Das Startdatum muss vor oder gleich dem Enddatum sein.'
        : null;

    const showCustom = () => {
        if (customProblem) return;
        setQuery({period: CUSTOM, from: customFrom, to: customTo});
    };

    const c = report && report.counts;
    const s = report && report.series;
    // Named after what the server says it used, not what the dropdown shows:
    // unusable custom dates fall back to Heute, and the captions must follow.
    const used = report && PERIODS.find(p => p.value === report.period);
    const caption = report && (report.period === CUSTOM ? report.label : used && used.text);
    const perBucket = s && s.granularity === 'hour' ? 'Pro Stunde' : 'Pro Tag';

    const reasons = report ? (allReasons ? report.reasons : report.reasons.slice(0, REASONS_COLLAPSED)) : [];

    const payment = c && [
        ['Stripe', c.subscriptions_by_provider.stripe, BLUE],
        ['PayPal', c.subscriptions_by_provider.paypal, ORANGE],
        ['Sonstige', c.subscriptions_by_provider.other, GREEN],
    ].filter(([name, value]) => name !== 'Sonstige' || value > 0);

    const cancellationsLabel = report && (report.cancellations_tracked_since
        ? `Kündigungen · seit ${report.cancellations_tracked_since}`
        : 'Kündigungen · noch nicht erfasst');

    return (
        <div className="kennzahlen">
            <div className="kz-hero">
                <div>
                    <h2>Kennzahlen</h2>
                    <div className="kz-hero-sub">
                        Getrennte Zählungen, keine Kette – die Zahlen sind keine Prozentwerte voneinander.
                    </div>
                </div>
                <div className="kz-hero-controls">
                    {/* Every value that changes sits alone in its element, as one
                        string. Chrome's page translation swaps text nodes for
                        copies; React then keeps updating the detached original,
                        and a label mixed in beside the icon froze on the old
                        range. A lone string child is rewritten whole instead. */}
                    {report && (
                        <span className="kz-chip" data-cy="kennzahlen-window">
                            <Icon name="calendar alternate outline" /><span>{report.label}</span>
                        </span>
                    )}
                    {/* No `compact`: it strips the dropdown's minimum width, which
                        leaves the longer options cramped and makes the control
                        resize every time the selection changes. */}
                    <Dropdown selection className="small kz-period"
                        options={PERIODS}
                        value={period}
                        onChange={(e, d) => choosePeriod(d.value)}
                        data-cy="kennzahlen-filter"
                    />
                    {loading && report && <Loader active inline size="tiny" inverted />}
                </div>
            </div>

            {period === CUSTOM && (
                <div className="kz-card kz-custom">
                    <span>von</span>
                    <Input type="date" size="small" value={customFrom}
                        max={customTo && customTo < today ? customTo : today}
                        onChange={(e, d) => setCustomFrom(d.value)}
                        data-cy="kennzahlen-from"
                    />
                    <span>bis</span>
                    <Input type="date" size="small" value={customTo}
                        min={customFrom || undefined}
                        max={today}
                        onChange={(e, d) => setCustomTo(d.value)}
                        data-cy="kennzahlen-to"
                    />
                    <Button size="small" type="button" className="kz-apply"
                        disabled={!!customProblem}
                        onClick={showCustom}
                        data-cy="kennzahlen-apply"
                    >
                        Anzeigen
                    </Button>
                    {customProblem && (
                        <span className="kz-custom-hint" data-cy="kennzahlen-date-hint">{customProblem}</span>
                    )}
                </div>
            )}

            {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}

            {/* The spinner only replaces the dashboard on the very first load. A
                filter change keeps the previous numbers on screen and fades
                them, because swapping everything out for a spinner on every
                click reads as the page breaking. */}
            {!report ? <Loader active inline="centered" style={{marginTop: 24}} /> : (
                <div className="kz-body" style={{opacity: loading ? 0.5 : 1}}>
                    <div className="kz-kpis">
                        <Kpi label="Anmeldungen" value={number(c.signups)} caption={caption}
                            icon="user plus" tint="blue" cy="kennzahlen-signups" />
                        <Kpi label="Profil vervollständigt" value={number(c.profiles_completed)} caption={caption}
                            icon="check circle outline" tint="orange"
                            cy="kennzahlen-profiles_completed" />
                        <Kpi label="Neue Abos" value={number(c.subscriptions)} caption={caption}
                            icon="credit card outline" tint="green"
                            cy="kennzahlen-subscriptions" />
                        <Kpi label="Umsatz aus neuen Abos" value={euros(c.revenue_cents)}
                            caption="Brutto, vor Erstattungen"
                            icon="chart line" tint="green" cy="kennzahlen-revenue" />
                        <Kpi label="Gesendete Nachrichten" value={number(c.messages_sent)} caption={caption}
                            icon="comment outline" tint="blue" cy="kennzahlen-messages_sent" />
                        <Kpi label="Aktive Mitglieder" value={number(c.message_senders)}
                            caption="Mindestens eine Nachricht geschrieben"
                            icon="users" tint="coral" cy="kennzahlen-message_senders" />
                    </div>

                    <div className="kz-minis">
                        <Mini label="E-Mail bestätigt" value={number(c.email_confirmed)}
                            icon="envelope outline" tint="blue" cy="kennzahlen-email_confirmed" />
                        <Mini label="Aktive Abos heute" value={number(c.active_subscriptions)}
                            icon="shield alternate" tint="green" cy="kennzahlen-active_subscriptions" />
                        <Mini label={cancellationsLabel}
                            value={report.cancellations_tracked_since ? number(c.cancellations) : '–'}
                            icon="times circle outline" tint="orange" cy="kennzahlen-cancellations" />
                        <Mini label="Widerrufe" value={number(c.withdrawals)}
                            icon="undo" tint="coral" cy="kennzahlen-withdrawals" />
                        <Mini label="Neue Unterhaltungen" value={number(c.new_conversations)}
                            icon="comments outline" tint="blue" cy="kennzahlen-new_conversations" />
                        <Mini label="Gratis-Gespräche genutzt" value={number(c.free_conversations)}
                            icon="gift" tint="coral" cy="kennzahlen-free_conversations" />
                    </div>

                    <Section>Anmeldungen</Section>
                    <div className="kz-card kz-block">
                        <CardHead title="Anmeldungen & vervollständigte Profile" sub={perBucket}>
                            <Legend items={[['Anmeldungen', BLUE], ['Profil vervollständigt', ORANGE]]} />
                        </CardHead>
                        <LineChart labels={s.labels} area series={[
                            {name: 'Anmeldungen', color: BLUE, data: s.signups},
                            {name: 'Profile', color: ORANGE, data: s.profiles_completed},
                        ]} />
                    </div>

                    <Section>Abos</Section>
                    <div className="kz-grid2">
                        <div className="kz-card">
                            <CardHead title="Neue Abos nach Laufzeit" sub={`${perBucket} · gestapelt`}
                                total={number(c.subscriptions)} />
                            <Legend items={[['1 Monat', BLUE], ['6 Monate', ORANGE], ['12 Monate', GREEN]]} />
                            <StackedBars labels={s.labels} height={190} series={[
                                {name: '1 Monat', color: BLUE, data: s.subscriptions_1},
                                {name: '6 Monate', color: ORANGE, data: s.subscriptions_6},
                                {name: '12 Monate', color: GREEN, data: s.subscriptions_12},
                            ]} />
                        </div>
                        <div className="kz-card">
                            <CardHead title="Zahlungsart" sub="Neue Abos im Zeitraum" />
                            <div className="kz-payment">
                                <Donut center={number(c.subscriptions)} sub="neu"
                                    items={payment.map(([name, value, color]) => ({name, value, color}))} />
                                <div className="kz-payment-bars">
                                    {payment.map(([name, value, color]) => {
                                        const share = c.subscriptions ? Math.round((100 * value) / c.subscriptions) : 0;
                                        return (
                                            <div key={name} className="kz-pbar-row"
                                                data-cy={`kennzahlen-provider-${name.toLowerCase()}`}>
                                                <div className="kz-pbar-head">
                                                    <span>{name}</span>
                                                    <span><b>{number(value)}</b> <em>{`· ${share} %`}</em></span>
                                                </div>
                                                <div className="kz-pbar"><b style={{width: `${share}%`, background: color}} /></div>
                                            </div>
                                        );
                                    })}
                                    <div className="kz-caption kz-plans" data-cy="kennzahlen-plans">
                                        {`Laufzeiten: ${number(c.subscriptions_by_plan['1'])} × 1 Monat · `
                                         + `${number(c.subscriptions_by_plan['6'])} × 6 Monate · `
                                         + `${number(c.subscriptions_by_plan['12'])} × 12 Monate`}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Section>Nachrichten</Section>
                    <div className="kz-card kz-block">
                        <CardHead title="Gesendete Nachrichten" sub={perBucket} total={number(c.messages_sent)} />
                        <LineChart labels={s.labels} height={210} area directLabels={false} series={[
                            {name: 'Nachrichten', color: BLUE, data: s.messages_sent},
                        ]} />
                    </div>

                    <Section>Rückmeldungen</Section>
                    <div className="kz-grid2 kz-last">
                        <div className="kz-card">
                            <CardHead title="Gelöschte Konten" sub={`${perBucket} · gestapelt`}
                                total={number(c.deleted_accounts)} />
                            <Legend items={[['Selbst gelöscht', BLUE], ['Wegen Inaktivität entfernt', ORANGE]]} />
                            <StackedBars labels={s.labels} height={250} series={[
                                {name: 'Selbst gelöscht', color: BLUE, data: s.deleted_by_member},
                                {name: 'Wegen Inaktivität', color: ORANGE, data: s.deleted_for_inactivity},
                            ]} />
                        </div>
                        <div className="kz-card" data-cy="kennzahlen-reasons">
                            <CardHead title="Gründe für die Kontolöschung" sub="Anonym · neueste zuerst"
                                total={number(c.deletion_reasons)} />
                            {reasons.length === 0 ? (
                                <div className="kz-empty">Keine Gründe im gewählten Zeitraum.</div>
                            ) : reasons.map((r, i) => (
                                <div key={i} className="kz-quote">
                                    <div className="kz-quote-text">{r.text}</div>
                                    <div className="kz-quote-date">{r.date}</div>
                                </div>
                            ))}
                            {report.reasons.length > REASONS_COLLAPSED && (
                                <button type="button" className="kz-more" onClick={() => setAllReasons(!allReasons)}>
                                    {allReasons ? 'Weniger anzeigen' : 'Alle Gründe anzeigen'}
                                </button>
                            )}
                            {allReasons && c.deletion_reasons > report.reasons.length && (
                                <div className="kz-caption">
                                    {`Die neuesten ${number(report.reasons.length)} von ${number(c.deletion_reasons)}.`}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MetricsAdmin;
